/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: contacts-search-view-component.ts
 *
 * Created by randysorensen on 4/23/18.
 */


function ContactsSearchViewController(
    this: JS5Object,
    _: JS5Object,
    $location: JS5Object,
    uiGridConstants: JS5Object,
    ContactsService: JS5Object,
    ModalService: JS5Object,
    NavbarUtilService: JS5Object,
    PaginationService: JS5Object,
    SearchFilterService: JS5Object,
    UtilsService: JS5Object
) {

    const getData = () => {
        this.loadingIndicator.show = true;

        if (_.get(this, 'gridApi.selection')) {
            this.gridApi.selection.clearSelectedRows();
        }

        this.searchText = $location.search().searchText || null;
        let offset = this.pagingOptions.pageSize * (this.pagingOptions.currentPage - 1);

        let queryOptions = {
            fetchOffset: offset,
            fetchLimit: this.pagingOptions.pageSize,
            searchString: this.searchText,
            includeHidden: this.showHidden
        };

        ContactsService.getContactsList(queryOptions).then((contacts: JS5Object[]) => {
            this.gridOptions.data = contacts;
        }).finally(() => {
            this.loadingIndicator.show = false;
        });
    };

    const getSelectedCount = (): number => {
        let selectedCount = 0;
        if (_.get(this, 'gridApi.selection')) {
            selectedCount = this.gridApi.selection.getSelectedCount();
        }
        return selectedCount;
    };

    const editContact = (contact: JS5Object): void => {
        $location.url(`/system/contacts/edit/${contact.id}`);
    };

    const toggleSelectedContactVisibility = (): void => {
        const selectedContact = this.gridApi.selection.getSelectedRows()[0];
        ContactsService.toggleContactVisibility(selectedContact.id)
            .finally(() => {
                getData();
            });
    };

    const showConfirmationModal = (action: string, handler: JS5Function): void => {
        let capitalizedAction: string = `${action[0].toUpperCase()}${action.slice(1)}`;
        ModalService.showMessageModal({
                messageText: `Are you sure you want to ${action} the selected contact?`,
                title: capitalizedAction + ' Contact',
                windowClass: 'confirm-modal-sm',
                showOK: true,
                okText: 'Yes',
                pullCancelLeft: true
            },
            handler
        );
    };

    this.onRowDblClick = (row: JS5Object): void => {
        editContact(row.entity);
    };

    this.onPaginationChanged = () => {
        getData();
    };

    this.paginationPageSizeChanged = () => {
        this.pagingOptions.currentPage = 1;
        this.onPaginationChanged();
    };

    this.paginationPrevPageHandler = () => {
        --this.pagingOptions.currentPage;
        this.onPaginationChanged();
    };

    this.paginationNextPageHandler = () => {
        ++this.pagingOptions.currentPage;
        this.onPaginationChanged();
    };

    const initGrid = () => {
        this.gridOptions = {
            rowSelection: true,
            enableFullRowSelection: true,
            enableRowHeaderSelection: false,
            enableSelectAll: false,
            multiSelect: false,
            enableColumnResizing: true,
            enableSorting: true,
            enableHorizontalScrollbar: uiGridConstants.ALWAYS,
            enableVerticalScrollbar: uiGridConstants.ALWAYS,
            rowHeight: 26,
            rowTemplate: 'components/visual/grid/searchview-component-row-template.html',
            onRegisterApi: (gridApi: JS5Object): void => {
                this.gridApi = gridApi;
            }
        };

        this.gridOptions.columnDefs = [
            {
                field: 'firstName',
                displayName: 'First Name',
                width: '17%',
                sort: {
                    direction: 'asc',
                    priority: 1
                },
                cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
            },
            {
                field: 'lastName',
                displayName: 'Last Name',
                width: '17%',
                sort: {
                    direction: 'asc',
                    priority: 0
                },
                cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
            },
            {
                field: 'title',
                displayName: 'Title',
                width: '17%',
                sort: {
                    direction: 'asc',
                    priority: 3,
                },
                cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
            },
            {
                field: 'emailAddress',
                displayName: 'Email Address',
                width: '*',
                sort: {
                    direction: 'asc',
                    priority: 2
                },
                cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
            },
            {
                field: 'phoneNumber',
                displayName: 'Phone Number',
                width: '17%',
                cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
            }];
    };

    const initToolbar = () => {
        this.toolbarButtonDefs = [
            {
                label: 'New',
                enabled: () => true,
                onClick: () => $location.url(`/system/contacts/edit/-1`)
            },
            {
                label: 'Edit',
                enabled: () => getSelectedCount() === 1,
                onClick: () => editContact(this.gridApi.selection.getSelectedRows()[0])
            },
            {
                label: 'Hide',
                enabled: () => getSelectedCount() === 1 && !this.gridApi.selection.getSelectedRows()[0].hidden,
                onClick: () => showConfirmationModal('hide', toggleSelectedContactVisibility)
            },
            {
                label: 'Unhide',
                enabled: () => getSelectedCount() === 1 && this.gridApi.selection.getSelectedRows()[0].hidden,
                onClick: () => showConfirmationModal('unhide', toggleSelectedContactVisibility)
            }
        ];

        this.toolbarCheckboxDefs = [
            {
                label: 'Show Hidden',
                onUpdate: (newValue: boolean): void => {
                    this.showHidden = newValue;
                    getData();
                },
                isChecked: this.showHidden
            }
        ];
    };

    const initPaging = () => {
        this.pagingOptions = PaginationService;
        this.pagingOptions.currentPage = 1;
        this.pagingOptions.pageSize = UtilsService.constants.DEFAULT_FETCH_LIMIT;

        this.paginationDefs = {
            paginationPrevPageHandler: this.paginationPrevPageHandler,
            paginationNextPageHandler: this.paginationNextPageHandler,
            paginationPageSizeChanged: this.paginationPageSizeChanged,
            paginationJumpToPageHandler: this.onPaginationChanged,
            pagingOptions: PaginationService,
        };
    };

    this.$onInit = () => {
        this.loadingIndicator = {
            backdrop: true,
            show: false,
            title: 'Working',
            message: 'Please wait...'
        };

        this.gridOptions = undefined;
        this.gridApi = undefined;

        this.toolbarButtonDefs = undefined;
        this.toolbarCheckboxDefs = undefined;
        this.showHidden = false;

        initGrid();
        initToolbar();
        initPaging();

        getData();

        NavbarUtilService.viewSearchFunction = this.getData;
        SearchFilterService.populateCriteriaFromLocationSearch();
    };

}

angular
    .module('prism-ui.system')
    .component('contactsSearchView', {
        bindings: {},
        templateUrl: 'system/contacts/searchView/contacts-search-view-template.html',
        controller: ContactsSearchViewController
    });
